import { useEffect, useState } from 'react';
import { Box, Button, TextareaAutosize, Typography } from '@mui/material';
import { Loading } from '@bumble/app/components/Loading';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.bmp'];
const TEXT_EXTENSIONS = ['.txt', '.log', '.csv', '.json'];

export type DocumentKind = 'image' | 'text' | 'unsupported';

export const getDocumentKind = (filename?: string): DocumentKind => {
  const lower = (filename ?? '').toLowerCase();
  if (IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext))) return 'image';
  if (TEXT_EXTENSIONS.some((ext) => lower.endsWith(ext))) return 'text';
  return 'unsupported';
};

interface DocumentViewerProps {
  filename?: string;
  url?: string;
}

const DownloadFallback = ({ url, message }: { url?: string; message: string }) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 3 }}>
      {message}
    </Typography>
    {url && (
      <Button variant="outlined" component="a" href={url} target="_blank" rel="noopener noreferrer">
        Download
      </Button>
    )}
  </Box>
);

export const DocumentViewer = ({ filename, url }: DocumentViewerProps) => {
  const kind = getDocumentKind(filename);
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(kind === 'text');
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (kind !== 'text' || !url) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((content) => {
        if (!cancelled) setText(content);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, url]);

  if (kind === 'image') {
    return <img src={url} width="100%" height={600} style={{ objectFit: 'contain' }} />;
  }

  if (kind === 'text') {
    if (loading) return <Loading height={200} />;
    if (loadError || text === null) {
      return <DownloadFallback url={url} message="Couldn't load a preview for this file. You can still download it below." />;
    }
    return (
      <TextareaAutosize
        style={{ width: '100%' }}
        minRows={20}
        value={text}
        readOnly
      />
    );
  }

  return <DownloadFallback url={url} message="Preview isn't supported for this file type. You can still download it below." />;
};
